import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Chip, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { EmptyState, GlassButton, LoadingOverlay } from 'dashboard/components/common';

/**
 * One day's bookings.
 *
 * A day, not a week or a list. The question someone behind a desk actually
 * asks is "who is coming today, and in what order" — a list of everything
 * ever booked answers a question nobody has, and a week view makes the
 * morning harder to read to save a click nobody minded.
 *
 * Ordered by time, because that is the order they will walk through the door.
 */

const STATUS_COLOUR = {
  confirmed: 'success',
  pending: 'warning',
  cancelled: 'default',
  completed: 'info',
  no_show: 'error',
};

const todayKey = () => new Date().toISOString().slice(0, 10);

const shiftDay = (key, days) => {
  const [y, m, d] = key.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return next.toISOString().slice(0, 10);
};

/** The time as the business reads it, from the instant we stored. */
const timeOf = (reservation, timeZone) => new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timeZone || undefined,
}).format(new Date(reservation.starts_at));

export default function DiaryTab({ brandId, resources }) {
  const [date, setDate] = useState(todayKey);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!brandId) return;
    setLoading(true);
    API.listReservations({ brand_id: brandId, date })
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Could not load the diary'))
      .finally(() => setLoading(false));
  }, [brandId, date]);

  useEffect(() => { load(); }, [load]);

  const zoneOf = (resourceId) => resources
    .find((r) => String(r._id) === String(resourceId))?.timezone;
  const nameOf = (resourceId) => resources
    .find((r) => String(r._id) === String(resourceId))?.name || '';

  const setStatus = (row, status) => {
    API.setReservationStatus({ brand_id: brandId, _id: row._id, status })
      .then(() => { toast.success(`Marked ${status.replace('_', ' ')}`); load(); })
      .catch((err) => toast.error(err?.message || 'Could not update it'));
  };

  const live = rows.filter((r) => r.status !== 'cancelled');
  const covers = live.reduce((n, r) => n + (Number(r.party_size) || 1), 0);

  return (
    <Box>
      <Stack direction='row' alignItems='center' gap='10px' flexWrap='wrap' mb='14px'>
        <IconButton size='small' onClick={() => setDate((d) => shiftDay(d, -1))} aria-label='Previous day'>
          <ChevronLeftIcon fontSize='small' />
        </IconButton>
        <TextField
          size='small'
          type='date'
          value={date}
          onChange={(e) => setDate(e.target.value || todayKey())}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 170 }}
        />
        <IconButton size='small' onClick={() => setDate((d) => shiftDay(d, 1))} aria-label='Next day'>
          <ChevronRightIcon fontSize='small' />
        </IconButton>
        <GlassButton size='small' onClick={() => setDate(todayKey())}>Today</GlassButton>
        <Box sx={{ flex: 1 }} />
        {/* Covers, not bookings. Six tables of two and one of twelve are very
            different evenings, and the kitchen needs the second number. */}
        <Typography variant='body2' color='text.secondary'>
          {live.length} booking{live.length === 1 ? '' : 's'} · {covers} {covers === 1 ? 'person' : 'people'}
        </Typography>
      </Stack>

      <LoadingOverlay loading={loading && rows.length === 0} minHeight={160}>
        {rows.length === 0 ? (
          <EmptyState>Nothing booked for this day.</EmptyState>
        ) : (
          <Stack spacing='8px'>
            {rows.map((row) => (
              <Stack
                key={row._id}
                direction='row'
                alignItems='center'
                gap='12px'
                flexWrap='wrap'
                sx={{
                  p: '12px 14px',
                  border: '1px solid', borderColor: 'divider', borderRadius: '12px',
                  opacity: row.status === 'cancelled' ? 0.55 : 1,
                }}
              >
                <Typography fontWeight='700' sx={{ width: 56, flexShrink: 0 }}>
                  {timeOf(row, zoneOf(row.resource_id))}
                </Typography>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight='600' noWrap>
                    {row.customer_name} · {row.party_size}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' noWrap display='block'>
                    {nameOf(row.resource_id)}
                    {row.customer_phone ? ` · ${row.customer_phone}` : ''}
                    {row.note ? ` · ${row.note}` : ''}
                  </Typography>
                </Box>
                <Chip size='small' label={row.reference} variant='outlined' />
                <Chip size='small' color={STATUS_COLOUR[row.status] || 'default'} label={row.status.replace('_', ' ')} />
                {row.status !== 'cancelled' && (
                  <Stack direction='row' gap='6px'>
                    {row.status === 'pending' && (
                      <GlassButton size='small' variant='contained' onClick={() => setStatus(row, 'confirmed')}>
                        Confirm
                      </GlassButton>
                    )}
                    <GlassButton size='small' onClick={() => setStatus(row, 'no_show')}>No show</GlassButton>
                    <GlassButton
                      size='small'
                      color='warning'
                      onClick={() => {
                        if (!window.confirm(`Cancel ${row.customer_name}'s booking?`)) return;
                        setStatus(row, 'cancelled');
                      }}
                    >
                      Cancel
                    </GlassButton>
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </LoadingOverlay>
    </Box>
  );
}
